import { action, flow } from 'mobx';
import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  FetchOptions,
  FetchPageOptions,
  StoreContainer
} from 'bernie-core';
import { Localization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import {
  FlightInformationStore,
  FreeCancellationStore,
  InformationCriteriaStore,
  InformationResultStore,
  MessagingCardsStore,
  BannersStore,
  PriceSummaryStore,
  PoliciesStore,
  ErrorStore,
  ClientMetadataStore,
  PageTitleStore,
  FlightsSelectedJourneyReviewStore,
  OpinionLabStore,
  StepIndicatorStore
} from 'stores';
import { generateRequestOptions, needSearchFis, needSearchBasisOnInfoKeys, PageId } from 'utility';
import { FlightsQueryState, FlightsShoppingPwaFlightInformationResults } from '__generated__/api';
import { DynamicElementsStore } from 'stores/dynamic-elements-store';

export class FlightInformationController implements Controller {
  public pageId = PageId.FIGHT_INFORMATION;
  public path = '/Flight-Information';
  public routeName = 'flight-information';
  public bundles = [];
  public exact = true;
  public component = codeSplit(() => import(/* webpackChunkName: "information" */ '../views/flight-information'));
  public pendingPromises = [];
  public clientErrorRenderDelay = 500;
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightInformation');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetch(options: FetchOptions): Promise<any> {
    if (options.isServer) {
      return this.fetchServerSide(options);
    }
    const searchNeeded = needSearchFis(options.query, false) && needSearchBasisOnInfoKeys(options.query);
    if (searchNeeded) {
      return this.fetchClientSide(options);
    }
    const bannersStore = options.stores.get<BannersStore>('bannersStore');
    bannersStore.setIsDialogEvent(!searchNeeded);

    return Promise.resolve();
  }

  public fetchPageData(pageData: FetchPageOptions) {
    const localizer = new Localization(pageData.context.locale);
    const title = localizer.formatText('fis.server.page.title');

    return Promise.resolve({ title: title });
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      flightInformationStore,
      informationResultStore,
      analyticsStore,
      freeCancellationStore,
      informationCriteriaStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      variables,
      flightsSelectedJourneyReviewStore,
      stepIndicatorStore
    } = this.getFetchVariables(options.stores, options.isServer);

    informationCriteriaStore.setCriteriaFromUrl(options);
    const response = await informationResultStore.fetchLoading(
      variables,
      generateRequestOptions(options.request, options.isServer, this.pageId),
      analyticsStore
    );

    if (!response) {
      return;
    }
    const { freeCancellation, priceSummary, serverErrorMessaging, clientMetadata, pageTitle, stepIndicator } = response;

    const flightsSelectedJourneyReview = response && response.flightsSelectedJourneyReview;

    freeCancellationStore.hydrate({ freeCancellation });
    // TODO: Add support for roundtrip & multidest
    flightsSelectedJourneyReviewStore.hydrate({ flightsSelectedJourneyReview });
    priceSummaryStore.hydrate({ priceSummary });
    errorStore.hydrate({ failedRequestMessaging: serverErrorMessaging });
    clientMetadataStore.hydrate(clientMetadata);
    pageTitleStore.hydrate(pageTitle);
    stepIndicatorStore.hydrate(stepIndicator);
    flightInformationStore.loading = true;
    errorStore.noApiResponse = false;
  }

  private fetchClientSide(options: FetchOptions) {
    if (this.pendingPromises.length > 0) {
      this.pendingPromises.forEach(p => p.cancel());
    }

    const clientPromise = this.createFlowForClientFetch(options, this.pageId);

    clientPromise.catch(error => {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    });

    this.pendingPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForClientFetch = flow(function* (options: FetchOptions, pageId: string) {
    const {
      flightInformationStore,
      informationResultStore,
      analyticsStore,
      messagingCardsStore,
      informationCriteriaStore,
      freeCancellationStore,
      priceSummaryStore,
      bannersStore,
      errorStore,
      clientMetadataStore,
      variables,
      policiesStore,
      pageTitleStore,
      flightsSelectedJourneyReviewStore,
      dynamicElementsStore,
      opinionLabStore
    } = this.getFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageId);
    informationCriteriaStore.setCriteriaFromUrl(options);
    flightInformationStore.loading = true;
    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaFlightInformationResults.FlightsDetail = yield informationResultStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );
      const messagingCards = response && response.messagingCards;
      const banners = response && response.banners;
      const clientMetadata = response && response.clientMetadata;
      const flightsSelectedJourneyReview = response && response.flightsSelectedJourneyReview;
      const dynamicElementsGroups = response && response.dynamicElementsGroups;
      const {
        freeCancellation,
        separateTicketBanner,
        priceSummary,
        serverErrorMessaging,
        brandPolicies,
        pageTitle,
        analytics,
        flightsOpinionLab
      } = response;
      freeCancellationStore.hydrate({ freeCancellation });
      messagingCardsStore.hydrate({ messagingCards, separateTicketBanner });
      priceSummaryStore.hydrate({ priceSummary });
      bannersStore.hydrate({ banners });
      errorStore.hydrate({ failedRequestMessaging: serverErrorMessaging });
      flightsSelectedJourneyReviewStore.hydrate({ flightsSelectedJourneyReview });
      policiesStore.hydrate({ brandPolicies });
      clientMetadataStore.hydrate(clientMetadata);
      pageTitleStore.hydrate(pageTitle);
      opinionLabStore.hydrate(flightsOpinionLab);
      flightInformationStore.hydrate({ analytics });
      dynamicElementsStore.hydrate({ dynamicElementsGroups: dynamicElementsGroups });
      dynamicElementsStore.initialiseDynamicStoreCache();
      if (options && options.query && options.query.identifier) {
        priceSummaryStore.setPriceSummary(dynamicElementsStore.getPriceSummaryById(options.query.identifier));
        flightsSelectedJourneyReviewStore.setFlightsSelectedJourneyReview(
          dynamicElementsStore.getFlightsJourneyDetailsById(options.query.identifier)
        );
      }
      flightsSelectedJourneyReviewStore.initialiseDynamicElementsIdentifier();

      pageTitleStore.loading = false;
      informationCriteriaStore.queryState = FlightsQueryState.LOADED;
      flightInformationStore.loading = false;
      this.pendingPromises = [];

      return;
    } catch (error) {
      errorStore.clientFetchFailure = true;
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    }
  });

  private getFetchVariables(stores: StoreContainer, isServer: boolean) {
    const informationCriteriaStore = stores.get<InformationCriteriaStore>('informationCriteriaStore');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    informationCriteriaStore.informationCriteria.queryState = loadSwitch;
    const variables = { context, ...informationCriteriaStore.informationCriteria };
    const flightInformationStore = stores.get<FlightInformationStore>('flightInformationStore');
    const informationResultStore = stores.get<InformationResultStore>('informationResult');
    const freeCancellationStore = stores.get<FreeCancellationStore>('freeCancellationStore');
    const messagingCardsStore = stores.get<MessagingCardsStore>('messagingCardsStore');
    const bannersStore = stores.get<BannersStore>('bannersStore');
    const priceSummaryStore = stores.get<PriceSummaryStore>('priceSummaryStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const policiesStore = stores.get<PoliciesStore>('policiesStore');
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const pageTitleStore = stores.get<PageTitleStore>('pageTitleStore');
    const opinionLabStore = stores.get<OpinionLabStore>('opinionLabStore');
    const flightsSelectedJourneyReviewStore = stores.get<FlightsSelectedJourneyReviewStore>(
      'flightsSelectedJourneyReviewStore'
    );
    const dynamicElementsStore = stores.get<DynamicElementsStore>('dynamicElementsStore');
    const stepIndicatorStore = stores.get<StepIndicatorStore>('stepIndicatorStore');

    return {
      analyticsStore,
      variables,
      flightInformationStore,
      informationResultStore,
      informationCriteriaStore,
      freeCancellationStore,
      messagingCardsStore,
      bannersStore,
      priceSummaryStore,
      errorStore,
      policiesStore,
      clientMetadataStore,
      pageTitleStore,
      opinionLabStore,
      flightsSelectedJourneyReviewStore,
      dynamicElementsStore,
      stepIndicatorStore
    };
  }
}
